import React, { useEffect, useMemo, useRef, useState } from 'react';
import useTranslate from '@src/i18n/useTranslate';
import { PaymentMethodCode } from '@src/core/buyerApiClient';

import {
  TextError,
  QRCode,
  TextCode,
  Description,
  Title,
  Value,
  CameraInstruction,
  Instruction,
  VerifyingPayment,
} from './styles';
import { PaymentMethodFormProps } from '..';
import Button from '@src/components/ui2/Button';
import Steps from './Steps';
import { MdOutlineCheckCircleOutline, MdOutlineSmartphone } from 'react-icons/md';
import { copyToClipboard, formatCents } from '@src/common';
import Loading from '@src/components/ui2/Loading';
import Hidden from '@src/components/ui2/Hidden';
import { FiAlertCircle } from 'react-icons/fi';
import Alert, { AlertTitle } from '@src/components/reusable/Alert';
import { checkInsufficientAmountError } from '@src/common/payment';

export const PixForm = (props: PaymentMethodFormProps) => {
  const { order, sale, directLink, paymentOption, onSubmit, saleStatusRequester, contextErrors } = props;
  const now = new Date();
  const { i18n, i18nElement } = useTranslate();

  const paymentEntity = order ?? sale ?? directLink;
  const lastTransaction = paymentEntity!.lastTransaction;
  const lastAttemptIsPendingPix =
    lastTransaction?.pending && lastTransaction.paymentMethod.code === PaymentMethodCode.Pix;

  const expiration = lastAttemptIsPendingPix ? new Date(lastTransaction.expiresAt!) : null;

  const isExpired = expiration != null ? expiration.getTime() < now.getTime() : false;
  const [syncInstant, setSyncInstant] = useState(now.getTime());
  const [showCopied, setShowCopied] = useState(false);
  const codeRef = useRef<HTMLTextAreaElement>(null);

  const hasInsufficientAmountError = useMemo(() => checkInsufficientAmountError(contextErrors), [contextErrors]);

  useEffect(() => {
    if (lastAttemptIsPendingPix) {
      saleStatusRequester();

      if (!isExpired) {
        const id = setTimeout(() => setSyncInstant(new Date().getTime()), 10000);
        return () => clearTimeout(id);
      }
    }

    return () => 1;
  }, [lastAttemptIsPendingPix, syncInstant]);

  const processPix = () => {
    onSubmit(paymentOption.paymentMethod.id);
  };

  const handleCodeToClipboard = (text: string) => {
    if (codeRef.current) {
      codeRef.current.select();
      copyToClipboard(text);

      setShowCopied(() => {
        setTimeout(() => {
          setShowCopied(false);
        }, 3000);
        return true;
      });
    }
  };

  if (!lastAttemptIsPendingPix || isExpired) {
    let errorMessage: string | null = null;

    if (contextErrors && 'transactionError' in contextErrors && contextErrors['transactionError']) {
      errorMessage = contextErrors['transactionError'].join(', ');
    } else if (isExpired) {
      errorMessage = i18n('checkout_expiredTry');
    }

    return (
      <>
        <Title>{i18n('direct_link_pix_form_step_1_title')}</Title>

        <Description>{i18n('direct_link_pix_form_step_1_description')}</Description>

        <Steps />

        <Value>
          {i18n('direct_link_pix_form_step_1_pix_amount')} {formatCents(paymentOption.amountCents)}
        </Value>

        <TextError $hasError={Boolean(errorMessage?.length)}>{errorMessage}</TextError>

        {hasInsufficientAmountError ? (
          <Alert sx={{ marginBottom: '16px' }} severity='error' icon={<FiAlertCircle color='inherit' size={24} />}>
            <AlertTitle>{i18n('checkout_pix_payment_insufficient_amount_title')}</AlertTitle>
            {i18n('checkout_pix_payment_insufficient_amount_description')}
          </Alert>
        ) : null}

        <Button onClick={() => processPix()} fullWidth>
          {i18n('direct_link_pix_form_step_1_finish')}
          <MdOutlineCheckCircleOutline />
        </Button>
      </>
    );
  }

  return (
    <>
      {lastTransaction?.pix ? (
        <>
          <Instruction>{i18nElement('direct_link_pix_form_step_2_instruction')}</Instruction>

          <Hidden smDown>
            <CameraInstruction>
              <MdOutlineSmartphone color='#4D4D4D' size={24} />
              {i18n('direct_link_pix_form_step_2_camera_instruction')}
            </CameraInstruction>

            <QRCode>
              <img alt={i18n('checkout_qrCodeWithPixCode')} src={lastTransaction.pix.qrCode} />
            </QRCode>
          </Hidden>

          <TextCode ref={codeRef} readOnly={true} value={lastTransaction.pix.code} />

          <Button onClick={() => handleCodeToClipboard(lastTransaction.pix!.code)} fullWidth outlined>
            {showCopied
              ? i18n('direct_link_pix_form_step_2_code_copied')
              : i18n('direct_link_pix_form_step_2_copy_code')}
          </Button>

          <Value $center>
            {i18n('direct_link_pix_form_step_1_pix_amount')} {formatCents(paymentOption.amountCents)}
          </Value>

          <VerifyingPayment>
            <Loading size={24} fullWidth={false} />
            {i18n('direct_link_pix_form_step_2_verifying_payment')}
          </VerifyingPayment>
        </>
      ) : null}
    </>
  );
};

export default PixForm;
